"use client";
import React from "react";
import Link from "next/link";
import Image, { StaticImageData } from "next/image";
import {
  SimpleGrid,
  Button,
  Divider,
  GridItem,
  Heading,
  Text,
  ResponsiveValue,
  Stack,
} from "@chakra-ui/react";
import { useTextVariants } from "@/theme/components/useSectionHeading";
import { HEADING_VARIANTS } from "@/theme/components/heading";
import { BUTTON_VARIANTS } from "@/theme/components/button";

export type CardProps = {
  text: string;
  image: string | StaticImageData;
  title: string;
  bgColor?: string;
  borderColor?: string;
  alignDirection?: string;
  headingColor?: string;
  textColor?: string;
  button?: {
    label: string;
    link: string;
  };
};

export const AdvantageCard = (props: CardProps) => {
  const {
    text,
    image,
    title,
    button,
    bgColor,
    borderColor,
    alignDirection,
    headingColor = "black",
    textColor = "text.100",
  } = props;
  const textVariant = useTextVariants();
  return (
    <Stack
      p={6}
      spacing={6}
      borderWidth={borderColor ? "1px" : "0px"}
      borderColor={borderColor}
      justifyContent={"space-between"}
      align={alignDirection}
      bgColor={bgColor}
      borderRadius={"12px"}
      boxShadow={"lg"}
      height="full"
      display="flex"
      flexDirection="column"
    >
      {image && (
        <Image alt={title} src={image} style={{ maxHeight: "50px", width: "52px" }} />
      )}
      <Heading
        as="h1"
        variant={HEADING_VARIANTS.SUBTITLE1}
        fontWeight={600}
        color={headingColor}
        textAlign={alignDirection === "center" ? "center" : "left"}
      >
        {title}
      </Heading>
      <Stack height={"100%"} justifyContent={"space-between"}>
        <Text
          variant={textVariant}
          textAlign={alignDirection === "center" ? "center" : "left"}
          color={textColor}
          whiteSpace="pre-line"
        >
          {text}
        </Text>
        {button && (
          <Button
            as={Link}
            href={button.link}
            variant={BUTTON_VARIANTS.IDEAL}
            w="fit-content"
            alignSelf={"center"}
            p={4}
            mt={6}
          >
            {button.label}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const AdvantageCardList = ({
  items,
  columns = { base: 1, md: 3 },
  bgColor = "transparent",
  alignDirection = "center",
}: {
  items: CardProps[];
  columns?: ResponsiveValue<number>;

  bgColor?: string;
  alignDirection?: string;
}) => {
  return (
    <>
      <SimpleGrid columns={columns} spacing={12} width={"100%"}>
        {items.map((item, index) => (
          <GridItem key={index}>
            <AdvantageCard {...item} bgColor={bgColor} alignDirection={alignDirection} />
          </GridItem>
        ))}
      </SimpleGrid>
      <Divider h={"4px"} />
    </>
  );
};

export default AdvantageCardList;
